/**
 * What a city builds, and what the buildings then do for it.
 *
 * Production is the second half of the economic spine: tiles make food and food makes
 * citizens (see index.ts), and those same tiles make production, which becomes units
 * and buildings. Everything a player does with a city that is not "grow" happens here.
 *
 * The two brakes on runaway growth also live here, and they are the reason this phase
 * mattered beyond the queue itself. Before them a city ran to size twelve by turn
 * forty because nothing at all stopped it:
 *
 *   - a SIZE CAP a building lifts, so growth needs infrastructure rather than time;
 *   - a SETTLER THAT COSTS POPULATION, so expanding is paid for out of the city that
 *     expands, which is what makes it a decision instead of a formality.
 */

import { discountFor, unitAvailable } from '../civs/traits.js';
import type { BuildingDef, Content } from '../../../content/schema/index.js';
import type { City, Civ, GameState, ProductionItem, TileGrid } from '../state/types.js';
import { neighborIndices, tileIndex } from '../state/grid.js';
import { hasTech, knows } from '../research/knowledge.js';

/** Definitions of every building a city has completed, in id order. */
export function buildingsOf(content: Content, city: City): BuildingDef[] {
  return city.buildings.map((id) => content.buildings.get(id));
}

export function hasBuilding(content: Content, city: City, key: string): boolean {
  const id = content.buildings.byKey.get(key)?.id;
  return id !== undefined && city.buildings.includes(id);
}

/** Sum one numeric effect across everything the city has built. */
export function effectTotal(
  content: Content,
  city: City,
  effect: 'productionBonus' | 'goldBonus' | 'sizeCapIncrease' | 'sciencePercent' | 'culture',
): number {
  let total = 0;
  for (const building of buildingsOf(content, city)) total += building.effects[effect] ?? 0;
  return total;
}

/** The largest share of the food box any building lets the city keep after growing. */
export function growthFoodKept(content: Content, city: City): number {
  let best = 0;
  for (const building of buildingsOf(content, city)) {
    best = Math.max(best, building.effects.growthFoodKept ?? 0);
  }
  return best;
}

/** How large this city can grow as it currently stands. */
export function sizeCap(content: Content, city: City): number {
  return content.rules.cities.baseSizeCap + effectTotal(content, city, 'sizeCapIncrease');
}

export function isAtSizeCap(content: Content, city: City): boolean {
  return city.population >= sizeCap(content, city);
}

/** The next building that would let this city grow past its limit, or null if none is left. */
export function capLifter(content: Content, city: City): BuildingDef | null {
  return content.buildings.all.find((b) => (b.effects.sizeCapIncrease ?? 0) > 0 && !city.buildings.includes(b.id)) ?? null;
}

/** "held at 14 — needs a sewer", for the panels that say why a city is not growing. */
export function capText(content: Content, city: City): string {
  const lifter = capLifter(content, city);
  const name = lifter?.name.toLowerCase();
  return lifter
    ? `held at ${sizeCap(content, city)} — needs ${/^[aeiou]/.test(name!) ? 'an' : 'a'} ${name}`
    : `held at ${sizeCap(content, city)} — as large as a city grows`;
}

/** What an item costs to build — for this civilisation, after its traits' discounts (ADR-0062). */
export function itemCost(content: Content, item: ProductionItem, civ?: Civ): number {
  const base = item.kind === 'unit' ? content.units.get(item.type).cost : content.buildings.get(item.building).cost;
  const discount = discountFor(content, civ, item);
  return discount > 0 ? Math.max(1, Math.round((base * (100 - discount)) / 100)) : base;
}

export function itemName(content: Content, item: ProductionItem): string {
  return item.kind === 'unit' ? content.units.get(item.type).name : content.buildings.get(item.building).name;
}

/** Whether two queue entries refer to the same thing. */
export function sameItem(a: ProductionItem, b: ProductionItem): boolean {
  if (a.kind === 'unit' && b.kind === 'unit') return a.type === b.type;
  if (a.kind === 'building' && b.kind === 'building') return a.building === b.building;
  return false;
}

/**
 * Why a city cannot build something right now, or null if it can.
 *
 * Separated from the command layer because the city screen needs the same answer in
 * order to grey out an entry, and two copies of this rule would drift apart.
 */
export function buildBlocker(city: City, item: ProductionItem): 'AlreadyBuilt' | 'AlreadyQueued' | null {
  if (item.kind === 'building') {
    if (city.buildings.includes(item.building)) return 'AlreadyBuilt';
    if (city.queue.some((q) => sameItem(q, item))) return 'AlreadyQueued';
  }
  return null;
}

/**
 * Whether a unit at the head of the queue is waiting on citizens rather than on
 * production. A settler cannot take the last person out of a city.
 */
export function heldForPopulation(content: Content, city: City, item: ProductionItem): boolean {
  if (item.kind !== 'unit') return false;
  const cost = content.units.get(item.type).populationCost;
  return cost > 0 && city.population <= cost;
}

/**
 * Whether a civilisation may build this at all: the technology is known, and — for a
 * unit — nothing has made it obsolete. Null means it may.
 */
export function techBlocker(content: Content, civ: Civ, item: ProductionItem): 'NeedsTech' | 'Obsolete' | 'OtherCivsUnit' | null {
  if (item.kind === 'unit') {
    const def = content.units.get(item.type);
    if (!unitAvailable(content, civ, def)) return 'OtherCivsUnit';
    if (!hasTech(civ, def.techId)) return 'NeedsTech';
    if (def.obsoleteTechId >= 0 && knows(civ, def.obsoleteTechId)) return 'Obsolete';
    return null;
  }
  return hasTech(civ, content.buildings.get(item.building).techId) ? null : 'NeedsTech';
}

/** The technology an item waits on, or -1. */
export function itemTech(content: Content, item: ProductionItem): number {
  return item.kind === 'unit' ? content.units.get(item.type).techId : content.buildings.get(item.building).techId;
}

/** Everything a city could be told to build. Order is content order, which is stable. */
/** Whether a city beside the open sea — not merely a lake — and so able to launch ships. */
export function isCoastalCity(content: Content, map: TileGrid, city: City): boolean {
  const lake = content.terrain.byKey.get('lake')?.id ?? -1;
  const centre = tileIndex(map, city.col, city.row);
  for (const n of neighborIndices(map, centre)) {
    const terrain = map.terrain[n]!;
    if (terrain !== lake && content.terrain.get(terrain).water) return true;
  }
  return false;
}

/** Whether where a city stands lets it build this: ships need the sea. */
export function placeAllows(content: Content, map: TileGrid, city: City, item: ProductionItem): boolean {
  if (item.kind !== 'unit') return true;
  return content.units.get(item.type).domain !== 'sea' || isCoastalCity(content, map, city);
}

export function buildableItems(content: Content, city: City, civ: Civ, map: TileGrid): ProductionItem[] {
  const items: ProductionItem[] = [];
  for (const unit of content.units.all) {
    const item: ProductionItem = { kind: 'unit', type: unit.id };
    if (!techBlocker(content, civ, item) && placeAllows(content, map, city, item)) items.push(item);
  }
  for (const building of content.buildings.all) {
    if (city.buildings.includes(building.id)) continue;
    // The palace is placed, never built: a civilisation's first city has it (ADR-0057).
    if (building.effects.isSeatOfGovernment) continue;
    const item: ProductionItem = { kind: 'building', building: building.id };
    if (!techBlocker(content, civ, item)) items.push(item);
  }
  return items;
}

/**
 * What the civilisation is close to being able to build: items whose technology it
 * could start researching right now. Shown greyed in the city screen, so the player
 * can see what the next discovery buys without opening the tree.
 */
export function comingSoon(content: Content, city: City, civ: Civ, map: TileGrid): ProductionItem[] {
  const ready = (techId: number): boolean =>
    techId >= 0 && !knows(civ, techId) && content.technologies.get(techId).prerequisites.every((p) => knows(civ, p));
  const items: ProductionItem[] = [];
  for (const unit of content.units.all) {
    const item: ProductionItem = { kind: 'unit', type: unit.id };
    if (ready(unit.techId) && unitAvailable(content, civ, unit) && placeAllows(content, map, city, item)) items.push(item);
  }
  for (const building of content.buildings.all) {
    if (!city.buildings.includes(building.id) && ready(building.techId)) items.push({ kind: 'building', building: building.id });
  }
  return items;
}

/**
 * What changing the head of the queue would cost in stored production.
 *
 * Switching between categories — a unit for a building, or the reverse — loses a share
 * of the store. Within a category it is free, as is reordering anything *behind* what
 * is being built: only the thing actually under construction has anything invested in
 * it.
 *
 * Exported because the city screen has to show this figure before the player commits.
 * A penalty discovered afterwards is a trap; a penalty shown on the button is a
 * decision, and it must be the same number in both places.
 */
export function switchCost(content: Content, city: City, next: ProductionItem | null): number {
  const current = city.queue[0];
  if (!current || !next) return 0;
  if (city.productionStore <= 0) return 0;
  if (current.kind === next.kind) return 0;
  return Math.floor(city.productionStore * content.rules.cities.switchPenalty);
}

/** Add a completed building and keep the list in id order, so saves stay canonical. */
export function addBuilding(city: City, building: number): void {
  if (city.buildings.includes(building)) return;
  city.buildings.push(building);
  city.buildings.sort((a, b) => a - b);
}

export function totalCities(state: GameState): number {
  return Object.keys(state.cities).length;
}
